'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { HttpEditorCodeMirrorHandle } from '@terra/ui-design-system';

type UseFileContentSearchOptions = {
  fileId?: string;
  isSearchEnabled?: boolean;
};

export function useFileContentSearch({
  fileId,
  isSearchEnabled = true,
}: UseFileContentSearchOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HttpEditorCodeMirrorHandle>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRegex, setIsRegex] = useState(false);
  const [isCaseSensitive, setIsCaseSensitive] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [matchCount, setMatchCount] = useState(0);

  useEffect(() => {
    setShowSearch(false);
    setSearchTerm('');
    setIsRegex(false);
    setIsCaseSensitive(false);
    setCurrentMatchIndex(0);
    setMatchCount(0);
  }, [fileId]);

  const handleToggleSearch = useCallback(() => {
    setShowSearch((previous) => {
      const next = !previous;

      if (next) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      } else {
        setSearchTerm('');
        setCurrentMatchIndex(0);
        setMatchCount(0);
        setTimeout(() => editorRef.current?.focus(), 0);
      }

      return next;
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const handleContainerKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!isSearchEnabled) {
        return;
      }

      if (
        !(event.metaKey || event.ctrlKey) ||
        event.key.toLowerCase() !== 'f'
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      handleToggleSearch();
    };

    container.addEventListener('keydown', handleContainerKeyDown, true);

    return () => {
      container.removeEventListener('keydown', handleContainerKeyDown, true);
    };
  }, [fileId, handleToggleSearch, isSearchEnabled]);

  useEffect(() => {
    if (!isSearchEnabled) {
      setShowSearch(false);
      setSearchTerm('');
      setCurrentMatchIndex(0);
      setMatchCount(0);
    }
  }, [isSearchEnabled]);

  const closeSearch = useCallback(() => {
    setShowSearch(false);
    setSearchTerm('');
    setCurrentMatchIndex(0);
    setMatchCount(0);
    setTimeout(() => editorRef.current?.focus(), 0);
  }, []);

  const handleSearchTermChange = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentMatchIndex(0);
  }, []);

  const handleNextMatch = useCallback(() => {
    if (matchCount === 0) {
      return;
    }

    setCurrentMatchIndex((previous) => (previous + 1) % matchCount);
  }, [matchCount]);

  const handlePrevMatch = useCallback(() => {
    if (matchCount === 0) {
      return;
    }

    setCurrentMatchIndex(
      (previous) => (previous - 1 + matchCount) % matchCount,
    );
  }, [matchCount]);

  const handleMatchCountChange = useCallback((count: number) => {
    setMatchCount(count);
    setCurrentMatchIndex((previous) =>
      count === 0 ? 0 : Math.min(previous, count - 1),
    );
  }, []);

  useEffect(() => {
    if (!showSearch || !searchTerm || matchCount === 0) {
      return;
    }

    editorRef.current?.scrollToCurrentMatch();
  }, [showSearch, searchTerm, currentMatchIndex, matchCount]);

  return {
    containerRef,
    editorRef,
    searchInputRef,
    showSearch,
    searchTerm,
    isRegex,
    isCaseSensitive,
    currentMatchIndex,
    matchCount,
    handleToggleSearch,
    closeSearch,
    handleSearchTermChange,
    handleNextMatch,
    handlePrevMatch,
    handleMatchCountChange,
    setIsRegex,
    setIsCaseSensitive,
  };
}
